import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Tags, Loader2, Check, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { qualityNcrAPI } from '@/lib/api';
import { ReasonSelect, useReasons, saveBlob, errMsg } from './NcrShared';

const GroupRow = ({ g, kind, reasons, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    setBusy(true);
    try { const r = (await qualityNcrAPI.classifyApply(kind, { key: g.key, reason })).data; toast.success(`${r.updated} row(s) set to ${r.reason}`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-[1fr_auto_220px_auto] gap-2 items-center border-b py-2 text-xs" data-testid={`classify-group-${g.key}`}>
      <div><p className="font-medium">{g.problem || g.key}</p><p className="text-[10px] text-muted-foreground truncate" title={g.numbers.join(', ')}>{g.numbers.slice(0, 4).join(', ')}{g.numbers.length > 4 ? ` +${g.numbers.length - 4}` : ''}</p></div>
      <span className="font-mono text-muted-foreground whitespace-nowrap">{g.count} row{g.count > 1 ? 's' : ''} · {g.quantity} pcs</span>
      <ReasonSelect value={reason} onChange={setReason} reasons={reasons} testid={`classify-reason-${g.key}`} />
      <Button size="sm" disabled={!reason || busy} onClick={apply} data-testid={`classify-apply-${g.key}`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}</Button>
    </div>
  );
};

export const ClassifyButton = ({ kind, onChanged }) => {
  const reasons = useReasons();
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState(null);
  const [filter, setFilter] = useState('');
  const load = useCallback(() => qualityNcrAPI.classifyGroups(kind).then((r) => setGroups(r.data)).catch(() => setGroups([])), [kind]);
  useEffect(() => { load(); }, [load]);
  if (!groups?.length && !open) return null;
  const total = (groups || []).reduce((s, g) => s + g.count, 0);
  const shown = (groups || []).filter((g) => g.key.includes(filter.trim().replace(/\s+/g, ' ').toUpperCase()));
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`classify-btn-${kind}`}><Tags className="w-4 h-4 mr-1" />Classify old rows ({total})</Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) onChanged(); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid={`classify-dialog-${kind}`}>
          <DialogHeader>
            <DialogTitle>Classify imported rows</DialogTitle>
            <DialogDescription>{total} imported rows have no rejection reason. Rows with the same problem text are grouped — pick a reason once per group.</DialogDescription>
          </DialogHeader>
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter problem text" className="h-9" data-testid="classify-filter-input" />
          {shown.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center" data-testid="classify-empty">All imported rows are classified</p>}
          {shown.map((g) => <GroupRow key={g.key} g={g} kind={kind} reasons={reasons} onDone={load} />)}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ExportRange = ({ label, exporter, fallback, testid }) => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (from && to && from > to) { toast.error('From date must be before To date'); return; }
    setBusy(true);
    try { saveBlob(await exporter({ date_from: from || undefined, date_to: to || undefined }), fallback); }
    catch (e) { toast.error(await errMsg(e, 'Export failed')); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1 border rounded-md pl-2" data-testid={`${testid}-range`}>
      <span className="text-[10px] text-muted-foreground">From</span>
      <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-36 border-0 shadow-none" data-testid={`${testid}-from`} />
      <span className="text-[10px] text-muted-foreground">To</span>
      <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-36 border-0 shadow-none" data-testid={`${testid}-to`} />
      <Button variant="outline" size="sm" onClick={run} disabled={busy} data-testid={`${testid}-btn`}>
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}{label}</Button>
    </div>
  );
};
